// ============================================
// PJE Configuration — Config-Driven Architecture
//
// Every tunable value lives here, not inline in engine code.
// HemoLogix Women defaults for MVP. When Med Co Maker ToS
// cascade goes live, configs are populated per-brand at runtime.
// ============================================

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { C_BRANDS, SC_PJE_CONFIG } from '../../constants/collections';
import type { RegisterType, SeverityLevel, NurseTraitProfile } from '../../types/patientJourney';

// --- Register Transition Rules ---

export interface RegisterTransitionRule {
  from: RegisterType;
  to: RegisterType;
  trigger: string;  // human-readable description
  condition: (state: { phase: string; severity: SeverityLevel; sentimentDeviation: boolean }) => boolean;
}

// --- PJE Config ---

export interface PJEConfig {
  brand: string;
  vertical: string;

  // Analyst Phase 1
  severityRules: {
    criticalThreshold: number;    // markers in critical range → 'critical' severity
    urgentThreshold: number;      // triggered markers → 'urgent' severity
    optimalRangeFallback: 'use_functional' | 'compute_inner_60' | 'explicit';
  };

  // Register system (3 registers: analyst, guide, blended)
  registerConfig: {
    prompts: Record<RegisterType, string>;
    phaseRegisterDefaults: {
      phase1_data: RegisterType;
      phase1_goals: RegisterType;
      phase1_protocol: RegisterType;
      phase2_freeform: RegisterType;
    };
    transitionRules: RegisterTransitionRule[];
  };

  // Modifier categories
  modifierCategories: {
    safetyCritical: string[];     // trigger immediate Phase 2 re-run on late reveal
    preference: string[];          // queue for end-of-conversation
  };

  // Scoring weights for 6 behavioral constraints (0-100 each)
  scoringWeights: {
    visualInterlocking: number;
    attributionCompliance: number;
    commercialFriction: number;
    interactiveVerification: number;
    preQualificationQuality: number;
    sentimentKillSwitch: number;
  };

  // Budget tier behavior
  budgetFiltering: 'informational' | 'structural';

  // Turn budgets
  turnBudget: {
    phase1Max: number;
    phase2Max: number;
    phase2CostCapCents: number;
  };

  // Sentiment
  sentimentKillSwitchPct: number;

  // Sentiment keyword scoring
  sentimentKeywords: {
    strongNegative: { words: string[]; score: number };
    moderateNegative: { words: string[]; score: number };
    positive: { words: string[]; score: number };
    defaultScore: number;
    baselineMessageCount: number;
  };

  // Nurse Trait Tuning (TARS-style calibration) — optional
  traitProfile?: NurseTraitProfile;

  // R4: Config source for audit trail (FIRESTORE or FALLBACK_WOMEN_DEFAULT)
  configSource?: 'FIRESTORE' | 'FALLBACK_WOMEN_DEFAULT';
}

// --- HemoLogix Women Defaults ---

export const HEMOLOGIX_WOMEN_CONFIG: PJEConfig = {
  brand: 'hemologix',
  vertical: 'hormones_women',

  severityRules: {
    criticalThreshold: 1,   // any marker in critical → 'critical' severity
    urgentThreshold: 3,     // 3+ triggered markers → 'urgent'
    optimalRangeFallback: 'use_functional',  // MVP: optimal = functional
  },

  registerConfig: {
    prompts: {
      analyst: [
        'ACT AS: The HemoLogix Analyst (Powered by MedGemma).',
        '',
        'TONE: Clinical, precise, objective, data-forward. Use structured formatting.',
        'Avoid fluff, greetings, or emotional validation.',
        '',
        'CORE TASKS:',
        '1. Data Mapping: Reference biomarker values against three ranges: Standard, Functional, and Optimal.',
        '2. Multi-Marker Correlation: Identify clinical patterns (e.g. Thyroid-Iron Loop, Inflammatory Clusters).',
        '3. Citations: Every functional/optimal range must reference clinical guidelines.',
        '',
        'CONSTRAINTS:',
        '- Never use "I feel" or "I\'m sorry."',
        '- Use quantitative language ("3.2 standard deviations from mean" vs "a bit high").',
        '- Format output in structured blocks or tables.',
        '- Reference at least 3 specific biomarker values by name, value, and status per response.',
      ].join('\n'),

      guide: [
        'ACT AS: The HemoLogix Guide (Created by 11:11).',
        '',
        'TONE: Warm, empathetic, slightly whimsical, and highly protective.',
        'You are the "Protective Fairy" or "Knowledgeable Friend" sitting on the user\'s shoulder.',
        '',
        'CORE TASKS:',
        '1. Meaning Translation: Translate the Analyst\'s data into what it means for THIS person\'s life.',
        '   Example: "Your ferritin at 22 might explain why you\'ve been dragging yourself to 3pm meetings."',
        '2. Emotional Anchoring: Validate the user\'s experience.',
        '   Example: "You weren\'t imagining it. Your body was trying to tell you something."',
        '3. Next Step Framing: Present the protocol as a path, not a prescription.',
        '   Example: "Here\'s what we can try together."',
        '',
        'CONSTRAINTS:',
        '- Never use clinical jargon without immediately explaining it.',
        '- Never present data without connecting it to felt experience.',
        '- Always offer a clear, achievable next step.',
        '- Match empathy to context layer (high stress = more validation, expert user = lighter framing).',
      ].join('\n'),

      blended: [
        'ACT AS: The HemoLogix AI Nurse — blending clinical precision with empathetic guidance.',
        '',
        'You have two modes available and should flow naturally between them:',
        '- ANALYST MODE: Lead with data. Reference specific biomarker values by name and number.',
        '- GUIDE MODE: Anchor with empathy. Connect data to the user\'s felt experience.',
        '',
        'Default behavior: present data first, then immediately translate to meaning.',
        'Example: "Your Vitamin D is at 22 ng/mL — the lab says that\'s fine, but functionally',
        'we\'d want 50-80. That might be why the afternoon fatigue has been so persistent."',
        '',
        'CONSTRAINTS:',
        '- Reference at least 3 specific biomarker values per substantive response.',
        '- Every data point must connect to the user\'s experience or goals.',
        '- Present protocol items as a path with clinical reasoning, not a shopping list.',
      ].join('\n'),
    },

    phaseRegisterDefaults: {
      phase1_data: 'analyst',
      phase1_goals: 'guide',
      phase1_protocol: 'blended',
      phase2_freeform: 'blended',
    },

    transitionRules: [
      {
        from: 'analyst',
        to: 'guide',
        trigger: 'User expresses emotional distress or confusion during data presentation',
        condition: ({ sentimentDeviation }) => sentimentDeviation,
      },
      {
        from: 'guide',
        to: 'analyst',
        trigger: 'User asks for specific data or evidence',
        condition: () => false,  // triggered by message content, not state — handled in registerEngine
      },
      {
        from: 'blended',
        to: 'guide',
        trigger: 'Sentiment deviation exceeds kill switch threshold',
        condition: ({ sentimentDeviation }) => sentimentDeviation,
      },
    ],
  },

  modifierCategories: {
    safetyCritical: ['medications', 'allergies', 'conditions', 'reproductiveStatus'],
    preference: ['lifestyle', 'budgetTier', 'intent', 'priorSupplements', 'goals'],
  },

  scoringWeights: {
    visualInterlocking: 15,
    attributionCompliance: 20,
    commercialFriction: 15,
    interactiveVerification: 10,
    preQualificationQuality: 25,
    sentimentKillSwitch: 15,
  },

  budgetFiltering: 'structural',  // v3.1: exclude higher tiers, not just reorder

  turnBudget: {
    phase1Max: 12,
    phase2Max: 20,
    phase2CostCapCents: 50,
  },

  sentimentKillSwitchPct: 30,

  sentimentKeywords: {
    strongNegative: {
      words: ['angry', 'frustrated', 'ridiculous', 'scam', 'waste', 'liar', 'terrible', 'awful', 'horrible'],
      score: -30,
    },
    moderateNegative: {
      words: ['confused', 'worried', 'expensive', 'not sure', 'disappointed', 'annoyed', 'doubt'],
      score: -15,
    },
    positive: {
      words: ['makes sense', 'helpful', 'thank you', 'interesting', 'great', 'perfect', 'appreciate', 'understand'],
      score: 10,
    },
    defaultScore: 0,
    baselineMessageCount: 2,
  },
};

// --- Config Cache ---
// Preloaded configs are cached in memory. getPJEConfig() reads from cache synchronously.
// Call loadPJEConfig() at service startup or before a test run to populate cache.

const configCache = new Map<string, { config: PJEConfig; loadedAt: number }>();
const CONFIG_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Load PJE config from Firestore for a specific brand.
 * Caches in memory for 5 minutes. Falls back to HEMOLOGIX_WOMEN_CONFIG.
 */
export async function loadPJEConfig(brandId: string): Promise<PJEConfig> {
  // Check cache first
  const cached = configCache.get(brandId);
  if (cached && Date.now() - cached.loadedAt < CONFIG_TTL_MS) {
    return cached.config;
  }

  try {
    const snap = await getDoc(doc(db, C_BRANDS, brandId, SC_PJE_CONFIG, 'current'));
    if (snap.exists()) {
      const data = snap.data() as Partial<PJEConfig>;
      // Merge with defaults — Firestore config overrides only what's set
      const merged: PJEConfig = {
        ...HEMOLOGIX_WOMEN_CONFIG,
        ...data,
        brand: brandId,
        // Deep merge nested objects
        severityRules: { ...HEMOLOGIX_WOMEN_CONFIG.severityRules, ...(data.severityRules || {}) },
        registerConfig: {
          ...HEMOLOGIX_WOMEN_CONFIG.registerConfig,
          ...(data.registerConfig || {}),
          prompts: { ...HEMOLOGIX_WOMEN_CONFIG.registerConfig.prompts, ...(data.registerConfig?.prompts || {}) },
          phaseRegisterDefaults: { ...HEMOLOGIX_WOMEN_CONFIG.registerConfig.phaseRegisterDefaults, ...(data.registerConfig?.phaseRegisterDefaults || {}) },
          transitionRules: data.registerConfig?.transitionRules || HEMOLOGIX_WOMEN_CONFIG.registerConfig.transitionRules,
        },
        modifierCategories: { ...HEMOLOGIX_WOMEN_CONFIG.modifierCategories, ...(data.modifierCategories || {}) },
        scoringWeights: { ...HEMOLOGIX_WOMEN_CONFIG.scoringWeights, ...(data.scoringWeights || {}) },
        turnBudget: { ...HEMOLOGIX_WOMEN_CONFIG.turnBudget, ...(data.turnBudget || {}) },
        sentimentKeywords: { ...HEMOLOGIX_WOMEN_CONFIG.sentimentKeywords, ...(data.sentimentKeywords || {}) },
        traitProfile: data.traitProfile
          ? { ...(HEMOLOGIX_WOMEN_CONFIG.traitProfile || {}), ...data.traitProfile }
          : HEMOLOGIX_WOMEN_CONFIG.traitProfile,
      };
      configCache.set(brandId, { config: merged, loadedAt: Date.now() });
      return merged;
    }
  } catch (error) {
    // R4: Firestore read failed — log and fall back to defaults
    console.warn('[PJEConfig] Firestore read failed — falling back to women default', {
      brandId,
      configSource: 'FALLBACK_WOMEN_DEFAULT',
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    });
  }

  // No brand-specific config found — cache default with configSource tagged
  const fallback: PJEConfig = {
    ...HEMOLOGIX_WOMEN_CONFIG,
    brand: brandId,
    // R4: Tag fallback so callers know config came from defaults, not Firestore
    configSource: 'FALLBACK_WOMEN_DEFAULT',
  };
  configCache.set(brandId, { config: fallback, loadedAt: Date.now() });
  return fallback;
}

/**
 * Pre-cache a config (used when config is already known, e.g., from a parent component).
 */
export function cachePJEConfig(brandId: string, config: PJEConfig): void {
  configCache.set(brandId, { config, loadedAt: Date.now() });
}

/**
 * Invalidate cached config for a brand (call after config update).
 */
export function invalidateConfigCache(brandId?: string): void {
  if (brandId) {
    configCache.delete(brandId);
  } else {
    configCache.clear();
  }
}

/** Get PJE config synchronously — reads from cache if available, otherwise returns default. */
export function getPJEConfig(brandId?: string): PJEConfig {
  if (brandId) {
    const cached = configCache.get(brandId);
    if (cached && Date.now() - cached.loadedAt < CONFIG_TTL_MS) {
      return cached.config;
    }
  }
  // R4: Synchronous path returns default with configSource tagged
  // (async path must be used to load from Firestore)
  return {
    ...HEMOLOGIX_WOMEN_CONFIG,
    ...(brandId ? { brand: brandId } : {}),
    configSource: 'FALLBACK_WOMEN_DEFAULT',
  };
}
